import React, { useState } from 'react';
import { Heading6 } from '../../common/GlobalElements/Typography';
import { StandardButton } from '../../common/GlobalElements/StandardButton';
import { ContainerWrapper } from '../../common/GlobalElements/Wrappers';
import { useIsMobile } from '../../../hooks/useIsMobile';

/**
 * Open/closed state for a collapsible section, persisted to sessionStorage so it
 * survives a remount (e.g. navigating away and back).
 * @param {string} storageKey sessionStorage key to persist the open state under
 * @param {boolean} defaultOpen Initial state when nothing is stored yet
 * @returns {[boolean, () => void]} [isOpen, toggle]
 */
function useCollapsibleState(storageKey, defaultOpen) {
    const [isOpen, setIsOpen] = useState(() => {
        try {
            const savedState = sessionStorage.getItem(storageKey);
            return savedState !== null ? JSON.parse(savedState) : defaultOpen;
        } catch (error) {
            return defaultOpen;
        }
    });

    const handleToggle = () => {
        setIsOpen((prev) => {
            const newState = !prev;
            try {
                sessionStorage.setItem(storageKey, JSON.stringify(newState));
            } catch (error) {}
            return newState;
        });
    };

    return [isOpen, handleToggle];
}

const ChevronButton = ({ isOpen, onClick, isMobile, className = '' }) => (
    <StandardButton
        width={isMobile ? 'auto' : '50px'}
        maxHeight="50px"
        type="button"
        className={`flex-shrink-0 ${className}`}
        onClick={onClick}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Collapse section' : 'Expand section'}
    >
        <i className={`fal fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
    </StandardButton>
);

/**
 * A single titled panel that expands/collapses via a chevron button, with its
 * open state persisted per `storageKey`.
 * @param {string} title
 * @param {React.ReactNode} children Content shown while expanded
 * @param {boolean} [defaultOpen] Initial state before anything is persisted
 * @param {string} storageKey Distinguishes this section's persisted state from others
 */
export const CollapsibleSection = ({ title, children, defaultOpen = false, storageKey }) => {
    const sessionStorageKey = `profileSection_${storageKey}`;
    const [isOpen, handleToggle] = useCollapsibleState(sessionStorageKey, defaultOpen);
    const isMobile = useIsMobile();

    return (
        <div className="d-flex" style={{ gap: isMobile ? 0 : '16px', marginBottom: '20px' }}>
            <ContainerWrapper variant="outline" className="flex-grow-1">
                {isMobile ? (
                    <div className="d-flex align-items-center justify-content-between">
                        <Heading6 className={isOpen ? 'mb-4' : 'mb-0'}>{title}</Heading6>
                        <ChevronButton
                            isOpen={isOpen}
                            onClick={handleToggle}
                            isMobile={isMobile}
                            className="align-self-start"
                        />
                    </div>
                ) : (
                    <Heading6 className={isOpen ? 'mb-4' : 'mb-0'}>{title}</Heading6>
                )}

                {isOpen && <div>{children}</div>}
            </ContainerWrapper>

            {!isMobile && <ChevronButton isOpen={isOpen} onClick={handleToggle} isMobile={isMobile} />}
        </div>
    );
};

/**
 * Like CollapsibleSection, but with a second, always-secondary panel shown
 * underneath the primary one whenever it's open.
 * @param {string} title
 * @param {React.ReactNode} children Primary panel content, shown while expanded
 * @param {string} [secondaryTitle]
 * @param {React.ReactNode} [secondaryContent]
 * @param {boolean} [showSecondary] Whether the secondary panel renders at all
 * @param {boolean} [defaultOpen] Initial state before anything is persisted
 * @param {string} storageKey Distinguishes this section's persisted state from others
 * @param {React.ReactNode} [toggle] Extra control rendered alongside the title
 */
export const DoubleCollapsibleSection = ({
    title,
    children,
    secondaryTitle,
    secondaryContent,
    showSecondary = true,
    defaultOpen = false,
    storageKey,
    toggle,
}) => {
    const sessionStorageKey = `profileSection_${storageKey}`;
    const [isOpen, handleToggle] = useCollapsibleState(sessionStorageKey, defaultOpen);
    const isMobile = useIsMobile();

    return (
        <div
            className="d-flex"
            style={{
                gap: isMobile ? 0 : '16px',
                marginBottom: '20px',
            }}
        >
            <div className="d-flex flex-column flex-grow-1" style={{ gap: '16px' }}>
                <ContainerWrapper variant="outline" className="">
                    {isMobile ? (
                        <div className={toggle && isOpen ? 'mb-3' : isOpen ? 'mb-4' : 'mb-0'}>
                            <div className="d-flex align-items-center justify-content-between">
                                <Heading6 className="mb-0">{title}</Heading6>
                                <ChevronButton
                                    isOpen={isOpen}
                                    onClick={handleToggle}
                                    isMobile={isMobile}
                                    className="align-self-start"
                                />
                            </div>

                            {toggle && isOpen && (
                                <div
                                    className="d-flex align-items-center justify-content-end"
                                    style={{ marginTop: '8px' }}
                                >
                                    {toggle}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div
                            className="d-flex align-items-center justify-content-between"
                            style={{ marginBottom: isOpen ? '16px' : 0 }}
                        >
                            <Heading6 className="mb-0">{title}</Heading6>
                            {toggle && isOpen && (
                                <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                                    {toggle}
                                </div>
                            )}
                        </div>
                    )}

                    {isOpen && <div>{children}</div>}
                </ContainerWrapper>

                {isOpen && showSecondary && (
                    <ContainerWrapper variant="outline" className="">
                        {secondaryTitle && <Heading6 className="mb-4">{secondaryTitle}</Heading6>}

                        {secondaryContent}
                    </ContainerWrapper>
                )}
            </div>

            {!isMobile && (
                <ChevronButton
                    isOpen={isOpen}
                    onClick={handleToggle}
                    isMobile={isMobile}
                    className="align-items-flex-start"
                />
            )}
        </div>
    );
};
